/**
 * DiffView — inline before/after word diff for a resolve proposal.
 *
 * The Fluent counterpart of the web editor's ``DiffView`` (asthra-v4-web): same
 * ``wordDiff`` engine, rendered with the add-in's design tokens instead of
 * Tailwind. Added words tint green, removed words tint red + strike-through,
 * unchanged text stays ink — so a reviewer reads the change at a glance before
 * Approving / Rejecting / Revising.
 *
 * SRP: pure presentation. It computes the diff from ``before``/``after`` and
 * renders it; it owns no state and never touches Word, the backend, or the SSOT.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors, surfaceColors, typeScale } from "../tokens";
import { htmlTablesAsText } from "../../services/formatter/htmlTable";
import { diffSummary, wordDiff } from "../services/wordDiff";

// Green (added) / red (removed) surfaces + text. Kept in-module rather than in
// tokens.ts because they exist only for this diff treatment; the greens/reds
// match the Issues-tab resolved/gap accents so the whole review surface reads
// as one system.
const ADDED_BG = "var(--success-soft)";
const ADDED_FG = "var(--success)";
const REMOVED_BG = "var(--danger-soft)";
const REMOVED_FG = "var(--danger)";

const useStyles = makeStyles({
  root: {
    fontFamily: fonts.body,
  },
  summary: {
    fontSize: typeScale.label,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    marginBottom: "6px",
  },
  body: {
    fontSize: typeScale.code,
    lineHeight: 1.5,
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowY: "auto",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
  },
  added: {
    backgroundColor: ADDED_BG,
    color: ADDED_FG,
    borderRadius: "2px",
  },
  removed: {
    backgroundColor: REMOVED_BG,
    color: REMOVED_FG,
    textDecorationLine: "line-through",
    borderRadius: "2px",
  },
});

export interface DiffViewProps {
  /** Original span text (before). */
  before: string;
  /** Proposed span text (after). */
  after: string;
  /** Max height of the scrollable diff body, px (default 200). */
  maxHeight?: number;
  /** Hide the "N added · M removed" summary line. */
  hideSummary?: boolean;
  className?: string;
}

export const DiffView: React.FC<DiffViewProps> = ({
  before,
  after,
  maxHeight = 200,
  hideSummary = false,
  className,
}) => {
  const styles = useStyles();
  const segments = wordDiff(htmlTablesAsText(before), htmlTablesAsText(after));
  const { added, removed } = diffSummary(segments);

  return (
    <div className={mergeClasses(styles.root, className)}>
      {!hideSummary ? (
        <div className={styles.summary}>
          {added} added · {removed} removed
        </div>
      ) : null}
      <div className={styles.body} style={{ maxHeight }}>
        {segments.map((seg, idx) => {
          const cls =
            seg.kind === "added" ? styles.added : seg.kind === "removed" ? styles.removed : undefined;
          // Segments are positional runs over the same text — index keys are stable
          // for a given (before, after) render.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional diff runs
          return cls ? (
            <span key={idx} className={cls}>
              {seg.text}
            </span>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional diff runs
            <span key={idx}>{seg.text}</span>
          );
        })}
      </div>
    </div>
  );
};

export default DiffView;
